import {
  Action,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  PlaybackOptions,
  Player,
  RequestType,
  SEEKING_STATES,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  TrackVariantInfo,
  NetworkRequestObservation,
} from '@quickplay/player-interface';
import { ActionFunctionMap, Interpreter, ServiceConfig } from 'xstate';
import { createAndInterpretPlayerStateMachine, PlaybackEvent, PLAYER_STATES, PlayerContext } from './StateMachine';
import { createThumbnailPreview, Factory } from './Factory';
import { createEventEmitter, Error, EventEmitter, Logger } from '@quickplay/foundation';
import { PlayerBuilder } from '../PlayerBuilder';
import { ThumbnailPreviewImpl } from '../thumbnailPreview/internal/ThumbnailPreviewImpl';
import { PlaybackPerformanceMonitor } from './PlaybackPerformanceMonitor';
import { createPlaybackPerformanceMonitor } from './PlaybackPerformanceMonitorImpl';
import { EngagementScoreModule } from './eqs/EngagementScoreModule';
import { normalizeFiniteNumber } from './eqs/EngagementScoreUtils';

function describeAutoplayFailure(error: unknown): string {
  const name = (error as { name?: string })?.name;
  if (name === 'NotAllowedError') {
    return 'autoplay blocked by browser policy';
  }
  if (name === 'NotSupportedError') {
    return 'autoplay not supported by this browser/device for the requested media';
  }
  return 'autoplay was blocked';
}

export function createCorePlayer(this: any, builder: PlayerBuilder): Player {
  let playerAdapter = Factory.createPlayerAdapter(builder);
  const logger: Logger = builder.configuredLogger;
  let playerContext = toPlayerContext(builder.options, 0, true);
  const playbackPerformanceMonitor: PlaybackPerformanceMonitor = createPlaybackPerformanceMonitor(() => playerAdapter);

  const eventEmitter: EventEmitter = createEventEmitter();
  let currentTime_ = 0;
  let duration_ = NaN;
  let currentEpochTime_: number | undefined;
  let initialPlaybackTime: number | undefined = playerContext.initialPlaybackTime;
  let playerState: string = PLAYBACK_STATES.IDLE,
    bufferingState: string = BUFFERING_STATES.INACTIVE,
    seekingState: string = SEEKING_STATES.INACTIVE;
  const isPlayerAdapterAvailable = (): boolean => playerAdapter !== null && playerAdapter !== undefined;
  const engagementScoreModule = builder.options.enableEngagementScore
    ? new EngagementScoreModule({
        videoElement: builder.videoElement,
        isLive: () => isPlayerAdapterAvailable() && playerAdapter.isLive,
        getTimingSnapshot: () => {
          if (!isPlayerAdapterAvailable()) return undefined;
          const statistics = playerAdapter.playbackStatistics;
          return { playTime: statistics.playTime, pauseTime: statistics.pauseTime };
        },
        getEligibleDuration: () => duration_,
        getPlaybackObservation: () => ({
          isSeeking: seekingState === SEEKING_STATES.ACTIVE,
          isBuffering: bufferingState === BUFFERING_STATES.ACTIVE,
          muteState: resolveMuteState(),
        }),
        onScore: score => eventEmitter.publish('engagementscore', score),
      })
    : undefined;

  const services: Record<string, ServiceConfig<PlayerContext, PlaybackEvent>> = {
    loadPlayer: () => {
      if (!isPlayerAdapterAvailable()) {
        return Promise.reject(new Error('Player adapter is not available'));
      }
      subscribePlayerAdapterEvents();

      return playerAdapter
        .load(initialPlaybackTime)
        .then(() => {
          eventEmitter.publish('contentloaded');
          return Promise.resolve();
        })
        .catch(e => {
          return Promise.reject(e);
        });
    },
  };

  const actions: ActionFunctionMap<PlayerContext, PlaybackEvent> = {
    incrementRetry: context => {
      context.retiresAttempted = context.retiresAttempted + 1;
    },

    playerOptionsChanged: (context, event) => {
      if (event.type === 'PLAYER_OPTIONS_CHANGED_EVENT') {
        context.autoPlayOnLoad = event.context.autoPlayOnLoad;
        context.maxRetries = event.context.maxRetries;
        context.repeatWhenFinished = event.context.repeatWhenFinished;
      }
    },

    enableAutoPlay: context => {
      context.autoPlayOnLoad = true;
    },

    playVideo: context => {
      if (!isPlayerAdapterAvailable()) {
        return;
      }
      // When autoPlayManagedByPlatform is enabled
      // irrespective of `initialPlayback` state to reflect the platform-managed
      // autoplay behavior when reusing preloaded instances. Otherwise, player
      // remains paused after preloaded instance restoration.
      if (context.autoPlayOnLoad && playerContext.autoPlayManagedByPlatform) {
        // do nothing since auto-play is managed by the underlying platform/player
        // e.g., UVP player on PS5, LG 2019
      } else {
        const playPromise = playerAdapter.play();
        // Some legacy runtimes (Tizen 2017, WebOS 2017) do not emit promise
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // Automatic playback failed (due to browser policies), update the player state to reflect true state
            playerAutoLoadFailed(error);
          });
        }
      }
      context.initialPlayback = false;
    },

    pauseVideo: () => {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.pause();
      }
    },

    stopVideo: () => {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.stop();
      }
    },

    abortPlayer: () => {
      if (!isPlayerAdapterAvailable()) {
        engagementScoreModule?.destroy();
        return;
      }
      playerAdapter.unload().finally(() => {
        unsubscribePlayerAdapterEvents();
        eventEmitter.publish('aborted');
        eventEmitter.reset();
        engagementScoreModule?.destroy();
        playerAdapter = null as any;
        // Clean up interpreter listener
        cleanupStateMachine();
      });
    },

    restartVideo: () => {
      if (!isPlayerAdapterAvailable()) {
        return;
      }
      playerAdapter.play().catch(_ => {
        // Automatic playback failed, update the SM to reflect true state
        playerAutoLoadFailed();
      });
    },

    seekVideo: (_context, event) => {
      if (event.type === 'SEEKING_START_EVENT') {
        playerAdapter.seek(event.time);
      }
    },
  };

  const interpreter: Interpreter<PlayerContext, any, PlaybackEvent> = createAndInterpretPlayerStateMachine(
    playerContext,
    actions,
    services,
  );

  const thumbnailPreview: ThumbnailPreviewImpl | undefined = createThumbnailPreview(
    builder.thumbnailPreviewEndpointUrl,
    builder.config,
  );

  const corePlayer: Player = {
    subscribe: eventEmitter.subscribe,
    unsubscribe: eventEmitter.unsubscribe,

    get playerState() {
      const machineStates: any = interpreter.state.value;
      return machineStates[PLAYER_STATES.PLAYER];
    },

    get isBuffering() {
      const machineStates: any = interpreter.state.value;
      return machineStates[PLAYER_STATES.BUFFERING] === BUFFERING_STATES.ACTIVE;
    },

    get internalPlayerVersion() {
      return playerAdapter.internalPlayerVersion;
    },

    get sdkVersion() {
      return '__buildVersion__';
    },

    get internalPlayerName() {
      return playerAdapter.internalPlayerName;
    },

    get isSeeking() {
      const machineStates: any = interpreter.state.value;
      return machineStates[PLAYER_STATES.SEEKING] === SEEKING_STATES.ACTIVE;
    },

    get currentTime() {
      return currentTime_;
    },

    get currentEpochTime() {
      /**
       * Precision Handling for Epoch Time
       *
       * Previously:
       * We were converting the high-precision epoch time to a Date object and back,
       * which caused loss of precision: new Date(this.currentTime).getTime()
       *
       * Issue:
       * This precision loss resulted in small drifts observed in the seekbar playhead.
       *
       * Solution:
       * We now use the original `currentEpochTime_` directly, preserving its full precision.
       * This eliminates the need for conversion and maintains accuracy in time-sensitive operations.
       */
      return currentEpochTime_ ? currentEpochTime_ : new Date(this.currentTime).getTime();
    },

    get duration() {
      return duration_;
    },

    get playbackStatistics() {
      if (!isPlayerAdapterAvailable()) {
        return undefined;
      }
      const snapshot = playerAdapter.playbackStatistics;
      return {
        ...snapshot,
        ...(this.isLive ? playbackPerformanceMonitor.statisticsFor(snapshot) : {}),
      };
    },

    get playbackRate() {
      return isPlayerAdapterAvailable() ? playerAdapter.playbackRate : 1;
    },

    set playbackRate(rate) {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.playbackRate = rate;
      }
    },

    get availableBitrates() {
      return isPlayerAdapterAvailable() ? playerAdapter.availableBitrates : [];
    },

    set minBitrate(minBitrate: number | undefined) {
      if (isPlayerAdapterAvailable() && minBitrate !== undefined) {
        playerAdapter.minBitrate = minBitrate;
      }
    },

    get minBitrate() {
      return isPlayerAdapterAvailable() ? playerAdapter.minBitrate : undefined;
    },

    set maxBitrate(maxBitrate: number | undefined) {
      if (isPlayerAdapterAvailable() && maxBitrate !== undefined) {
        playerAdapter.maxBitrate = maxBitrate;
      }
    },

    get maxBitrate() {
      return isPlayerAdapterAvailable() ? playerAdapter.maxBitrate : undefined;
    },

    get rawPlayer() {
      return isPlayerAdapterAvailable() ? playerAdapter.rawPlayer : undefined;
    },

    get rawLibrary() {
      return isPlayerAdapterAvailable() ? playerAdapter.rawLibrary : undefined;
    },

    get isLive() {
      return isPlayerAdapterAvailable() ? playerAdapter.isLive : false;
    },

    get isMute() {
      return isPlayerAdapterAvailable() ? playerAdapter.isMute : false;
    },

    get playbackOptions() {
      return isPlayerAdapterAvailable() ? playerAdapter.playbackOptions : undefined;
    },

    get volume() {
      return isPlayerAdapterAvailable() ? playerAdapter.volume : 1;
    },

    set volume(volumeLevel: number) {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.volume = volumeLevel;
      }
    },

    load(): void {
      duration_ = Number.NaN;
      interpreter.send('LOAD_EVENT');
    },

    play(): void {
      interpreter.send('PLAY_EVENT');
    },

    pause(): void {
      interpreter.send('PAUSE_EVENT');
    },

    abort: (error: Error) => {
      eventEmitter.publish('error', error);
      engagementScoreModule?.finalize('error');
      interpreter.send('ABORT_EVENT');
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      if (eventName === StreamTimelineEvent.LIVE_PROGRAM_START) {
        engagementScoreModule?.programBoundary(metadata);
      }
      playbackPerformanceMonitor.onStreamTimelineEvent(eventName);

      eventEmitter.publish('streamtimelineevent', eventName, action, metadata);
    },

    seek(seekPoint: number): void {
      // if seekPoint is NaN, do not send the event
      if (isNaN(seekPoint)) {
        return;
      }
      interpreter.send('SEEKING_START_EVENT', { time: seekPoint });
      const machineStates: any = interpreter.state.value;
      if (
        machineStates[PLAYER_STATES.PLAYER] === PLAYBACK_STATES.LOADING ||
        machineStates['player'] === PLAYBACK_STATES.IDLE
      ) {
        initialPlaybackTime = seekPoint;
      } else {
        interpreter.send('SEEKING_START_EVENT', { time: seekPoint });
      }
    },

    seekToLiveEdge(): void {
      if (this.isLive) {
        this.seek(playerAdapter.seekableRange().end);
      }
    },

    seekableRange(): SeekRangeInfo {
      if (!isPlayerAdapterAvailable()) {
        return { start: 0, end: 0 };
      }
      return playerAdapter.seekableRange();
    },

    currentOffsetFromLiveEdge(): number {
      if (!isPlayerAdapterAvailable()) {
        return 0;
      }
      return playerAdapter.currentOffsetFromLiveEdge();
    },

    muteOrUnmute(): void {
      if (isPlayerAdapterAvailable() && playerAdapter.muteOrUnmute) {
        playerAdapter.muteOrUnmute();
      }
    },

    getAllTracks(): TrackInfo[] {
      if (!isPlayerAdapterAvailable()) {
        return [];
      }
      return playerAdapter.getAllTracks();
    },

    getActiveTracks(): TrackInfo[] {
      if (!isPlayerAdapterAvailable()) {
        return [];
      }
      return playerAdapter.getActiveTracks();
    },

    getVariantTracks(): TrackVariantInfo[] {
      if (!isPlayerAdapterAvailable()) {
        return [];
      }
      return playerAdapter.getVariantTracks();
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      if (!isPlayerAdapterAvailable()) {
        return [];
      }
      return playerAdapter.getAvailableTrackVariants(trackType);
    },

    selectTrack(track?: TrackInfo): void {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.selectTrack(track);
      }
    },

    setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.setRequestInterceptor(requestInterceptor);
      }
    },

    setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.setResponseInterceptor(responseInterceptor);
      }
    },

    accessFunctionHandler(json: string): void {
      if (isPlayerAdapterAvailable() && playerAdapter.accessFunctionHandler) {
        playerAdapter.accessFunctionHandler(json);
      }
    },

    stop(): Promise<void> {
      engagementScoreModule?.finalize('exit');
      interpreter.send('STOP_EVENT');
      if (!isPlayerAdapterAvailable()) {
        engagementScoreModule?.destroy();
        return Promise.resolve();
      }
      return playerAdapter.unload().finally(() => {
        unsubscribePlayerAdapterEvents();
        eventEmitter.reset();
        engagementScoreModule?.destroy();
        playerAdapter = null as any;
        // Clean up interpreter listener
        cleanupStateMachine();
        return Promise.resolve();
      });
    },

    //returns metadata for the corresponding position
    async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      if (!isPlayerAdapterAvailable()) {
        return undefined;
      }
      if (thumbnailPreview) {
        return thumbnailPreview.getThumbnail(position);
      }
      return playerAdapter.getThumbnail(position);
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      if (!isPlayerAdapterAvailable()) {
        return;
      }
      playerAdapter.setPlaybackOptions(playbackOptions);

      playerContext = toPlayerContext(playbackOptions, playerContext.duration, playerContext.initialPlayback);

      interpreter.send('PLAYER_OPTIONS_CHANGED_EVENT', { context: playerContext });

      initialPlaybackTime = playerContext.initialPlaybackTime;
    },

    restrictQuality(enableRestriction: boolean): void {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.restrictQuality(enableRestriction);
      }
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      if (!isPlayerAdapterAvailable()) {
        return [];
      }
      return playerAdapter.availableMimeTypes(trackType);
    },

    unloadAndSavePreload(): Promise<void> {
      if (!isPlayerAdapterAvailable()) {
        return Promise.resolve();
      }
      return playerAdapter.unloadAndSavePreload();
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      if (isPlayerAdapterAvailable()) {
        return playerAdapter.setPreferredMimeTypes(trackType, mimeTypes);
      }
    },

    restoreFromPreload(): void {
      if (isPlayerAdapterAvailable()) {
        playerAdapter.load();
      }
    },
  };

  /**
   * Returns a new player context.
   * @param playbackOptions
   * @param duration
   * @param initialPlayback
   */
  function toPlayerContext(playbackOptions: PlaybackOptions, duration = 0, initialPlayback = true): any {
    return {
      duration: duration,
      initialPlayback: initialPlayback,
      shouldRetryOnErrors: playbackOptions.shouldRetryOnErrors,
      maxRetries: playbackOptions.maxRetries,
      retiresAttempted: playbackOptions.retiresAttempted,
      autoPlayOnLoad: playbackOptions.autoPlayOnLoad,
      autoPlayManagedByPlatform: playbackOptions.autoPlayManagedByPlatform,
      repeatWhenFinished: playbackOptions.repeatWhenFinished,
      initialPlaybackTime: playbackOptions.initialPlaybackTime,
    };
  }

  // Todo: Use xstate.onMicrostep instead of transistion event.
  // https://spectrum.chat/statecharts/general/transient-states-not-detected~7c28fb80-b622-41f0-acca-857891648e08
  // All the transient states are not tracked now.
  interpreter.onTransition(state => {
    if (!state.changed) {
      return;
    }

    logger.info(`Player state has changed ${JSON.stringify(state.value)}`);

    Object.entries(state.value).forEach(([stateKey, stateValue]) => {
      const value = stateValue.toString();
      switch (stateKey) {
        case 'buffering':
          if (bufferingState !== value) {
            eventEmitter.publish('bufferingstatechanged', value);
          }
          bufferingState = value;
          break;
        case 'player':
          if (playerState !== value) {
            eventEmitter.publish('playbackstatechanged', value);
          }
          playerState = value;
          break;
        case 'seeking':
          if (seekingState !== value) {
            eventEmitter.publish('seekingstatechanged', value);
          }
          seekingState = value;
          break;
      }
    });
  });

  interpreter.start();

  const subscribeEvents = {
    //Todo: Disscss and implement seeking and buffeing events.
    bufferingstart: (): void => {
      interpreter.send('BUFFERING_START_EVENT');
    },
    bufferingend: (): void => {
      interpreter.send('BUFFERING_END_EVENT');
    },
    buffererror: onBufferError,
    manifestHttpError: onManifestHttpError,
    playerseeked: onSeeked,
    playerpaused: onPause,
    playerstarted: onPlay,
    playerended: onEnded,
    error: onError,
    playeridle: onPlayerIdle,
    timeupdate: onTimeUpdate,
    livetovodtransition: (): void => corePlayer.send(StreamTimelineEvent.LIVE_EVENT_PROGRESS_END, Action.NONE),
    trackschanged: (): void => eventEmitter.publish('trackschanged'),
    textchanged: (): void => eventEmitter.publish('textchanged'),
    audiotrackchanged: (): void => eventEmitter.publish('audiotrackchanged'),
    trackavailabilitychanged: (): void =>
      eventEmitter.publish('trackavailabilitychanged', playerAdapter.getAllTracks()),
    licenseupdate: (): void => eventEmitter.publish('licenseupdate'),
    timedmetadata: (event: any): void => eventEmitter.publish('timedmetadata', event),
    daitimelineregionenter: (event: any): void => eventEmitter.publish('daitimelineregionenter', event),
    bandwidthchanged: (): void => eventEmitter.publish('bandwidthchanged'),
    ratechanged: (): void => eventEmitter.publish('ratechanged'),
    volumechanged: (): void => eventEmitter.publish('volumechanged'),
    manifestupdated: (): void => eventEmitter.publish('manifestupdated'),
    manifestparsed: (): void => eventEmitter.publish('manifestparsed'),
    networkrequest: (observation: NetworkRequestObservation): void => eventEmitter.publish('networkrequest', observation),
    networkresponse: (observation: NetworkRequestObservation): void => eventEmitter.publish('networkresponse', observation),
  };

  function onSeeked(currentTime: number): void {
    currentTime_ = currentTime;
    if (isPlayerAdapterAvailable() && playerAdapter.isLive && Number.isFinite(currentTime)) {
      currentEpochTime_ = currentTime;
    } else {
      currentEpochTime_ = undefined;
    }
    interpreter.send('SEEKING_END_EVENT');
  }

  function onPause(): void {
    // Note: Handling the case where application developer uses the video element controls directly.
    if (corePlayer.playerState !== PLAYBACK_STATES.PAUSED) interpreter.send('PLAYER_PAUSED_EVENT');
  }

  function onPlay(): void {
    engagementScoreModule?.start();

    if (corePlayer.playerState === PLAYBACK_STATES.PAUSED) interpreter.send('PLAYER_PLAYED_EVENT');

    // Note: Handling the case where application developer uses the video element controls directly.
    if (
      (corePlayer.playerState === PLAYBACK_STATES.LOADED && playerContext.autoPlayOnLoad) ||
      corePlayer.playerState !== PLAYBACK_STATES.STARTED
    )
      return;

    interpreter.send('PLAYER_PLAYED_EVENT');
  }

  // Note: This handles aborting the playback with manifest error due to bad http status
  function onManifestHttpError(error: Error): void {
    corePlayer.abort(error);
  }

  function onEnded(): void {
    engagementScoreModule?.finalize('completed');
    interpreter.send('PLAYER_ENDED_EVENT');
    eventEmitter.publish('streamended');
  }

  function onError(error: Error): void {
    corePlayer.abort(error);
  }
  // Note: This handles aborting the playback with buffer timeout error
  function onBufferError(error: Error): void {
    corePlayer.abort(error);
  }

  function onPlayerIdle(): void {
    engagementScoreModule?.finalize('exit');
    interpreter.send('PLAYER_IDLE_EVENT');
    eventEmitter.publish('streamended');
  }

  function playerAutoLoadFailed(error?: unknown): void {
    // Automatic playback did not start and the player remains paused. This is most commonly
    // caused by the browser's autoplay policy (e.g. no prior user interaction, unmuted autoplay
    // blocked) or an underlying device/platform limitation restricting autoplay. Logged via
    // console.warn (not the configured logger) so this is visible even when the app hasn't
    // opted into a logger configuration (default logger level is OFF).
    console.warn(describeAutoplayFailure(error), error);
    interpreter.send('PLAYER_PAUSED_EVENT');
  }

  function cleanupStateMachine(): void {
    interpreter.stop();
  }

  function onTimeUpdate(currentTime: number, duration: number, currentEpochTime?: number): void {
    engagementScoreModule?.observe();
    currentTime_ = currentTime;
    currentEpochTime_ = currentEpochTime;
    duration_ = duration;

    if (!isNaN(duration_)) {
      eventEmitter.publish('progressupdate', currentTime, currentEpochTime, duration);
    }
  }

  function resolveMuteState(): 'muted' | 'unmuted' | 'unknown' {
    if (!isPlayerAdapterAvailable()) return 'unknown';
    if (typeof playerAdapter.isMute === 'boolean') return playerAdapter.isMute ? 'muted' : 'unmuted';
    const volume = normalizeFiniteNumber(playerAdapter.volume);
    if (volume === undefined) return 'unknown';
    return volume === 0 ? 'muted' : 'unmuted';
  }

  function subscribePlayerAdapterEvents(): void {
    if (isPlayerAdapterAvailable()) {
      playerAdapter.subscribe(subscribeEvents);
    }
  }

  function unsubscribePlayerAdapterEvents(): void {
    if (isPlayerAdapterAvailable()) {
      playerAdapter.unsubscribe(subscribeEvents);
    }
  }

  return corePlayer;
}
